import {
    IExternalAgent,
    IExternalAgentEnvelope,
    IExternalAgentResponse,
    ITaskChannel,
    fromResponse,
    toPayload,
} from "../components/ExternalAgents/IExternalAgent";
import Api from "../config/api";

/**
 * External agent endpoints.
 *
 * The API is not uniform: the list returns a bare array while create and update return
 * {success, message, data}. Every method here returns the same normalised IExternalAgent so
 * callers do not have to know which. The previous signatures all claimed Promise<any[]> —
 * including the ones that return a single record — which is how the edit screen came to POST
 * where it meant to PUT without anything complaining.
 */

interface IAxiosList {
    data: IExternalAgentResponse[]
}

interface IAxiosOne {
    data: IExternalAgentResponse
}

interface IAxiosEnvelope {
    data: IExternalAgentEnvelope
}

export interface IToggleResult {
    available: boolean
}

interface IAxiosToggle {
    data: { available?: boolean }
}

export interface ISaveResult {
    agent: IExternalAgent
    /** False when the record saved but its Twilio worker did not. Undefined on create. */
    twilioSynced?: boolean
    message?: string
}

export class ExternalAgentsActions {
    static get(): Promise<IExternalAgent[]> {
        return Api.get("/external-agents")
            .then((res: IAxiosList) => (res.data || []).map(fromResponse))
    }

    static read(id: number): Promise<IExternalAgent> {
        return Api.get("/external-agents/" + id)
            .then((res: IAxiosOne) => fromResponse(res.data))
    }

    static create(agent: IExternalAgent): Promise<ISaveResult> {
        return Api.post("/external-agents", toPayload(agent))
            .then((res: IAxiosEnvelope) => ({
                agent: fromResponse(res.data.data),
                message: res.data.message,
            }))
    }

    static update(id: number, agent: IExternalAgent): Promise<ISaveResult> {
        return Api.put("/external-agents/" + id, toPayload(agent))
            .then((res: IAxiosEnvelope) => ({
                agent: fromResponse(res.data.data),
                twilioSynced: res.data.twilio_synced,
                message: res.data.message,
            }))
    }

    static delete(id: number): Promise<void> {
        return Api.delete("/external-agents/" + id).then(() => undefined)
    }

    static toggle(id: number): Promise<IToggleResult> {
        return Api.get("/external-agents/" + id + "/toggle")
            .then((res: IAxiosToggle) => ({ available: res.data.available === true }))
    }
}

/**
 * The server's own words, where it has any.
 *
 * `error.toString()` yields "Error: Request failed with status code 422" and throws away the
 * validation messages that say which field is wrong — which is all an operator needs.
 */
export function describeAgentError(error: any): string {
    const data = error?.response?.data

    if (data?.errors && typeof data.errors === "object") {
        const messages = Object.keys(data.errors)
            .map((key) => ([] as string[]).concat(data.errors[key]).join(" "))
            .filter((m) => m.length > 0)

        if (messages.length > 0) {
            return messages.join("\n")
        }
    }

    // Laravel 5.2 returns a bare {field: [messages]} map for a 422.
    if (error?.response?.status === 422 && data && typeof data === "object") {
        const messages = Object.keys(data)
            .map((key) => ([] as string[]).concat(data[key]).join(" "))
            .filter((m) => m.length > 0)

        if (messages.length > 0) {
            return messages.join("\n")
        }
    }

    if (typeof data?.message === "string") {
        return data.message
    }

    if (typeof data?.error === "string") {
        return data.error
    }

    return error?.message || "Request failed"
}

/**
 * Task channels live only in TaskRouter — there is no local row — so both calls are
 * read-through to /external-agents/{id}/channels. Mirrors the internal-agent pair at
 * /agents/{agentId}/channels.
 */
export class ExternalAgentChannelsActions {
    static get(agentId: number): Promise<ITaskChannel[]> {
        return Api.get("/external-agents/" + agentId + "/channels")
            .then((res: { data: ITaskChannel[] }) => res.data || [])
    }

    static update(
        agentId: number,
        sid: string,
        change: { capacity: number; available: boolean }
    ): Promise<ITaskChannel> {
        return Api.put("/external-agents/" + agentId + "/channels/" + sid, change)
            .then((res: { data: ITaskChannel }) => res.data)
    }
}
